import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  Stack,
  Text,
} from '@fluentui/react';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { CloseButton } from 'common/components/Buttons';
import { useCommonStyles } from 'common/styles';
import { EntityType } from 'common/types/utility';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { setTripsModalState } from '../../..';
import {
  TripDelete,
  TripDeleteVariables,
} from '../../../__generated__/TripDelete';
import { Trip_trip } from '../../__generated__/Trip';
import { TripCreateFormValues } from '../../types';
import { useStyles } from './index.styles';
import { setTripInitiateFlow } from 'utility/cache/ui';
import { CloneTrip } from './CloneTrip';
import { SectionProps } from '../..';
import { TravelAuthorization_travelAuthorization } from 'travelAuthorization/TravelPlan/view/__generated__/TravelAuthorization';
import { TripItemProps } from '../../../TripList';
import TRIP_DELETE from '../../../TripDelete.graphql';
export const TRIP_MODAL_HEADER_HEIGHT = 70;
interface HeaderProps {
  tripData: Trip_trip | null | undefined;
  onIsLoading: (param: boolean) => void;
  inputsDisabled: boolean;
  onClose?: () => void;
  showCloneOption: boolean;
  tripItemsState: Map<string, SectionProps>;
  travelAuthorizationData:
    | Partial<TravelAuthorization_travelAuthorization>
    | null
    | undefined;
  isTripDetailModal: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  tripData,
  onIsLoading,
  inputsDisabled,
  onClose,
  showCloneOption,
  tripItemsState,
  travelAuthorizationData,
  isTripDetailModal,
}) => {
  const commonStyles = useCommonStyles();
  const [clonedTripId, setClonedTripId] = useState<string | undefined>(
    undefined
  );
  const styles = useStyles();

  const {
    tripLocator,
    id,
    _isDeletable,
    _rowTimestamp,
    travelAuthorizationId,
  } = { ...tripData };
  const isNew = !id;

  const {
    formState: { isDirty, dirtyFields },
  } = useFormContext<TripCreateFormValues>();

  const [deleteItem, { loading: deleteItemLoading }] = useMutation<
    TripDelete,
    TripDeleteVariables
  >(TRIP_DELETE, {
    errorPolicy: 'all',
  });

  const onDeleteClick = async () => {
    const { errors } = await deleteItem({
      variables: {
        input: {
          entityDelete: [
            {
              id: id!,
              rowTimestamp: _rowTimestamp!,
            },
          ],
        },
        id: travelAuthorizationId!,
      },
    });
    if (errors)
      showCustomToast({
        content: errors?.[0].message,
        appearance: 'error',
      });
    else {
      if (!isTripDetailModal)
        setTripsModalState({
          trip: undefined,
          isModalOpen: false,
          isNew: false,
        });
      else onClose?.();
      showCustomToast({
        content: 'Trip Item - Deleted Successfully',
        appearance: 'success',
      });
    }
  };

  useEffect(() => {
    onIsLoading(deleteItemLoading);
  }, [deleteItemLoading]);

  useEffect(() => {
    if (clonedTripId) {
      const { nodes } = { ...travelAuthorizationData?.trips };
      const transformedData = nodes?.map(
        (item, index) =>
          ({
            ...item,
          } as TripItemProps)
      );
      const newTrip = transformedData?.find((item) => item.id === clonedTripId);

      setTripsModalState({
        trip: newTrip,
        isModalOpen: true,
        isNew: false,
      });
    }
  }, [clonedTripId, travelAuthorizationData]);

  return (
    <Stack horizontal className={styles.container}>
      <Stack
        horizontal
        verticalAlign="center"
        horizontalAlign="space-between"
        className={styles.innerContainer}
      >
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <Text variant="xLarge">{`${'Trip'}`}</Text>
          {tripLocator && (
            <Text variant="xLarge" className={commonStyles.colorThemePrimary}>
              {tripLocator}
            </Text>
          )}
        </Stack>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
          {showCloneOption && (
            <CloneTrip
              tripItemsState={tripItemsState}
              tripData={tripData}
              setClonedTripId={setClonedTripId}
            />
          )}

          <ActionMessageModal
            visible={!isNew}
            entityType={EntityType.Trip}
            disabled={!_isDeletable || inputsDisabled}
            onConfirm={onDeleteClick}
          />

          <CancelButton
            onConfirm={() => {
              if (!isTripDetailModal) {
                setTripsModalState({
                  trip: undefined,
                  isModalOpen: false,
                  isNew: true,
                });

                setTripInitiateFlow('none');
              }
              onClose?.();
            }}
            isDirty={isDirty && Object.keys(dirtyFields).length > 0}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const dialogStyles = { main: { maxWidth: 450 } };

const cancelDialogContentProps = {
  type: DialogType.normal,
  title: 'Warning',
  closeButtonAriaLabel: 'Close',
  subText: 'Are you sure you want to leave without saving changes?',
};

export const CancelButton: React.FC<{
  isDirty: boolean;
  onConfirm: () => void;
}> = ({ isDirty, onConfirm }) => {
  const [visible, setVisible] = useState(false);
  const modalProps = React.useMemo(
    () => ({
      isBlocking: false,
      styles: dialogStyles,
    }),
    []
  );

  return (
    <Stack>
      {visible && (
        <Dialog
          hidden={false}
          dialogContentProps={cancelDialogContentProps}
          modalProps={modalProps}
        >
          <DialogFooter>
            <PrimaryButton
              onClick={() => {
                onConfirm();
                setVisible(false);
              }}
              text="Leave"
            />
            <DefaultButton onClick={() => setVisible(false)} text="Cancel" />
          </DialogFooter>
        </Dialog>
      )}

      <CloseButton
        onClick={() => {
          if (isDirty) {
            setVisible(true);
          } else onConfirm();
        }}
      />
    </Stack>
  );
};
